import { makeStyles } from '@material-ui/styles';
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Datetime from 'react-datetime';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  warningColor,
  whiteColor,
} from 'assets/jss/material-dashboard-pro-react';
import { parseFloatOrNull, parseIntOrNull } from 'helpers/parseNumberOrNull';
import SweetAlert from 'react-bootstrap-sweetalert';
import { Dialog } from '@material-ui/core';
import classNames from 'classnames';
import buttonStyle from 'assets/jss/material-dashboard-pro-react/components/buttonStyle';
import { get, post, put } from 'helpers/apiHelpers';
import FormControlStickyButton from 'components/FormControlStickyButton/FormControlStickyButton';

import { fetchDiets } from '../../actions/Diets';
import BasicInfoSection from './formComponents/BasicInfoSection';
import ScopeOfActivitySections from './formComponents/ScopeOfActivitySections';
import RestrictionsSection from './formComponents/RestrictionsSection';
import GeolocationRestrictionsSection from './formComponents/GeolocationRestrictionsSection';
import PlatformsSection, {
  buildEmptyPlatformsState,
} from './formComponents/PlatformsSection';
import useBrandConsentsAvailability from './hooks/useBrandConsentsAvailability';

const buildPlatformsStateFromResponse = responsePlatforms => {
  const state = buildEmptyPlatformsState();

  (responsePlatforms || []).forEach(
    ({ '@id': iri, platform, overrideDiscount, discount }) => {
      if (state[platform]) {
        state[platform] = {
          enabled: true,
          overrideDiscount: overrideDiscount || false,
          discount: discount ?? null,
          '@id': iri ?? null,
        };
      }
    }
  );

  return state;
};

const VALUES_TO_CONFIRM = [
  {
    key: 'code',
    descriptionI18nKey: 'discountCodes.confirmChangesAlert.code',
  },
];

const getEditedDataKeys = (current, snapshot) => {
  const editedData = [];

  Object.keys(current).forEach(key => {
    if (current[key] !== snapshot[key]) {
      editedData.push(key);
    }
  });

  return editedData;
};

const transformStateToData = (
  basicInfoSection,
  restrictionsSection,
  scopeOfActivitySection,
  hasGeolocationRestrictions,
  importedDiscountCodes
  // eslint-disable-next-line @typescript-eslint/max-params -- FIXME
) => ({
  description: basicInfoSection.description,
  code: basicInfoSection.code.replace(/  +/g, ' ').trim(),
  validFrom: restrictionsSection.startDate || null,
  validTo: restrictionsSection.endDate || null,
  isPercentage: basicInfoSection.isPercent,
  discount: scopeOfActivitySection.isFreeDelivery
    ? 0
    : parseFloatOrNull(basicInfoSection.value),
  quantity: parseIntOrNull(basicInfoSection.amount),
  limit: parseIntOrNull(restrictionsSection.limit),
  limitPerUser: parseIntOrNull(restrictionsSection.limitPerUser),
  limitPerSubscription: parseIntOrNull(
    restrictionsSection.limitPerSubscription
  ),
  minimalOrderValue: restrictionsSection.isMinimalOrderValue
    ? parseIntOrNull(restrictionsSection.minimalOrderValue)
    : null,
  minimalDietLength: restrictionsSection.isMinimalDietLength
    ? parseIntOrNull(restrictionsSection.minimalDietLength)
    : null,
  onlyFirstOrder: restrictionsSection.onlyFirstOrder,
  requiredEmailConsent: restrictionsSection.requiredEmailConsent,
  requiredSmsConsent: restrictionsSection.requiredSmsConsent,
  requiredPhoneConsent: restrictionsSection.requiredPhoneConsent,
  disableMoneybox: restrictionsSection.disableMoneybox,
  disableDietDiscount: scopeOfActivitySection.isDisableDietDiscount,
  freeDelivery: scopeOfActivitySection.isFreeDelivery,
  excludeDeliveryFromDiscount:
    scopeOfActivitySection.isExcludeDeliveryFromDiscount,
  applyToAddons: scopeOfActivitySection.isApplyToAddons,
  diets:
    scopeOfActivitySection.isSelectedDiets && scopeOfActivitySection.dietsState
      ? scopeOfActivitySection.dietsState
      : [],
  geolocalizationRestrictions: hasGeolocationRestrictions,
  postCodes: importedDiscountCodes,
  platforms: Object.entries(restrictionsSection.platforms || {})
    .filter(([, config]) => config.enabled)
    .map(([platform, config]) => ({
      '@id': config['@id'],
      platform,
      overrideDiscount: config.overrideDiscount,
      discount: config.overrideDiscount ? config.discount : null,
    })),
});

const useStyles = makeStyles({
  button: buttonStyle.button,
  success: buttonStyle.success,
  buttonArchive: {
    background: warningColor[0],
    color: whiteColor,
    marginLeft: '0',
    marginRight: 'auto',
  },
  valuesToConfirmList: {
    paddingInlineStart: '0',
    margin: '0.5rem 0',
  },
  sweetAlertTitle: {
    whiteSpace: 'nowrap',
  },
  sweetAlertContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
});

const Form = ({ history, isEdit }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const params = useParams();
  const classes = useStyles();

  const diets = useSelector(state => state.Diets.diets);

  const [importedDiscountCodes, setImportedDiscountCodes] = useState([]);
  const [isArchived, setIsArchived] = useState(false);
  const [editedValuesToConfirm, setEditedValuesToConfirm] = useState([]);

  const [isLoading, setIsLoading] = useState(isEdit);

  const [basicInfoSection, setBasicInfoSection] = useState({
    code: '',
    description: '',
    value: '',
    isPercent: true,
    amount: '',
  });

  const [scopeOfActivitySection, setScopeOfActivitySection] = useState({
    isSelectedDiets: false,
    dietsState: [],
    isApplyToAddons: false,
    isDisableDietDiscount: false,
    isFreeDelivery: false,
    isExcludeDeliveryFromDiscount: false,
  });

  const [restrictionsSection, setRestrictionsSection] = useState({
    onlyFirstOrder: false,
    requiredEmailConsent: false,
    requiredSmsConsent: false,
    requiredPhoneConsent: false,
    isStartDate: false,
    isEndDate: false,
    startDate: '',
    endDate: '',
    limit: '',
    limitPerUser: '',
    limitPerSubscription: 0,
    minimalOrderValue: null,
    minimalDietLength: null,
    isLimit: false,
    isLimitPerUser: false,
    isLimitPerSubscription: true,
    isMinimalOrderValue: false,
    isMinimalDietLength: false,
    disableMoneybox: false,
    restrictPlatforms: false,
    platforms: buildEmptyPlatformsState(),
  });

  const dataResponseSnapshot = useRef({});

  const [hasGeolocationRestrictions, setHasGeolocationRestrictions] =
    useState(false);

  const brandConsentsAvailability = useBrandConsentsAvailability();

  const yesterday = Datetime.moment().subtract(1, 'day');

  useEffect(() => {
    const fetchData = async () => {
      dispatch(fetchDiets());

      if (isEdit) {
        setIsLoading(true);

        try {
          const res = await get(`/discount-codes/${params.id}`);
          const hasValidTo = res.validTo !== null;
          const hasValidFrom = res.validFrom !== null;

          const basicInfoSectionResponse = {
            code: res.code,
            description: res.description,
            value: res.discount,
            isPercent: res.isPercentage,
            amount: res.quantity || 1,
          };

          const scopeOfActivitySectionResponse = {
            isSelectedDiets: res.diets.length > 0,
            dietsState: res.diets,
            isApplyToAddons: res.applyToAddons,
            isDisableDietDiscount: res.disableDietDiscount,
            isFreeDelivery: res.freeDelivery,
            isExcludeDeliveryFromDiscount: res.excludeDeliveryFromDiscount,
          };

          const platformsState = buildPlatformsStateFromResponse(res.platforms);

          const restrictionsSectionResponse = {
            onlyFirstOrder: res.onlyFirstOrder,
            requiredEmailConsent: res.requiredEmailConsent ?? false,
            requiredSmsConsent: res.requiredSmsConsent ?? false,
            requiredPhoneConsent: res.requiredPhoneConsent ?? false,
            isStartDate: hasValidFrom,
            isEndDate: hasValidTo,
            startDate: hasValidFrom ? Datetime.moment(res.validFrom) : '',
            endDate: hasValidTo ? Datetime.moment(res.validTo) : '',
            limit: res.limit,
            limitPerUser: res.limitPerUser,
            limitPerSubscription: res.limitPerSubscription,
            minimalOrderValue: res.minimalOrderValue,
            minimalDietLength: res.minimalDietLength,
            isLimit: res.limit !== null,
            isLimitPerUser: res.limitPerUser !== null,
            isLimitPerSubscription: res.limitPerSubscription !== null,
            isMinimalOrderValue: res.minimalOrderValue !== null,
            isMinimalDietLength: res.minimalDietLength !== null,
            disableMoneybox: res.disableMoneybox || false,
            restrictPlatforms: Object.values(platformsState).some(
              platform => platform.enabled
            ),
            platforms: platformsState,
          };

          setBasicInfoSection(basicInfoSectionResponse);
          setScopeOfActivitySection(scopeOfActivitySectionResponse);
          setRestrictionsSection(restrictionsSectionResponse);

          const transformedData = transformStateToData(
            basicInfoSectionResponse,
            restrictionsSectionResponse,
            scopeOfActivitySectionResponse,
            hasGeolocationRestrictions
          );

          dataResponseSnapshot.current = transformedData;

          setIsArchived(res.archived);
          setImportedDiscountCodes(res.postCodes);
          setHasGeolocationRestrictions(res.geolocalizationRestrictions);
        } catch (error) {
          // eslint-disable-next-line no-console -- FIXME
          console.log(error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, []);

  const startDateValidation = current =>
    current.isAfter(yesterday) && restrictionsSection.endDate
      ? current.isSameOrBefore(restrictionsSection.endDate)
      : current.isAfter(yesterday);

  const endDateValidation = picked => {
    if (restrictionsSection.startDate !== '') {
      const dayBeforeStartDate = Datetime.moment(
        restrictionsSection.startDate
      ).subtract(1, 'day');
      return picked.isSameOrAfter(dayBeforeStartDate);
    }
    return picked.isAfter(yesterday);
  };

  const validateDates = () => {
    if (restrictionsSection.startDate && restrictionsSection.endDate) {
      return restrictionsSection.endDate.isAfter(restrictionsSection.startDate);
    }
    return true;
  };

  const validateCheckedStartDate = () =>
    restrictionsSection.isStartDate && !restrictionsSection.startDate;
  const validateCheckedEndDate = () =>
    restrictionsSection.isEndDate && !restrictionsSection.endDate;
  const validateForm = () =>
    basicInfoSection.code &&
    basicInfoSection.amount &&
    (scopeOfActivitySection.isFreeDelivery || basicInfoSection.value);

  const performValidations = () => {
    if (!validateForm()) {
      toast.error(t('form.fillAllRequiredFields'));
      return;
    }
    if (!validateDates()) {
      toast.error(
        `${t('discountCodes.incorrectDatesValid')} ${t('discountCodes.info1')}`
      );
      return;
    }
    if (validateCheckedStartDate()) {
      toast.error(t('discountCodes.info2'));
      return;
    }
    if (validateCheckedEndDate()) {
      toast.error(t('discountCodes.info3'));
      return;
    }
    if (
      restrictionsSection.restrictPlatforms &&
      !Object.values(restrictionsSection.platforms).some(
        platform => platform?.enabled
      )
    ) {
      toast.error(t('discountCodes.platforms.noPlatformSelected'));
      return;
    }
    if (basicInfoSection.amount > 1 && !basicInfoSection.code.includes('*')) {
      toast.info(
        `${t('discountCodes.info4')} ${t('discountCodes.info5')} ${t(
          'brandCfg.etc'
        )}${basicInfoSection.code}-*`
      );
      return;
    }

    // eslint-disable-next-line no-negated-condition -- FIXME
    if (!isEdit) {
      // eslint-disable-next-line no-use-before-define -- FIXME
      handleSubmit();
    } else {
      const data = transformStateToData(
        basicInfoSection,
        restrictionsSection,
        scopeOfActivitySection,
        hasGeolocationRestrictions,
        importedDiscountCodes
      );

      const editedDataKeys = getEditedDataKeys(
        data,
        dataResponseSnapshot.current
      );

      // eslint-disable-next-line no-shadow -- FIXME
      const editedValuesToConfirm = VALUES_TO_CONFIRM.filter(item =>
        editedDataKeys.includes(item.key)
      );

      if (editedValuesToConfirm.length > 0) {
        setEditedValuesToConfirm(editedValuesToConfirm);
      } else {
        // eslint-disable-next-line no-use-before-define -- FIXME
        handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    const data = transformStateToData(
      basicInfoSection,
      restrictionsSection,
      scopeOfActivitySection,
      hasGeolocationRestrictions,
      importedDiscountCodes
    );

    try {
      if (isEdit) {
        await put(`/discount-codes/${params.id}`, data);
      } else {
        await post('/discount-codes', data);
      }
      history.push('/admin/discount-codes');
    } catch (error) {
      if (
        error.response?.data?.violations &&
        error.response.data.violations[0].propertyPath === '[0].code'
      ) {
        toast.error(t('discountCodes.info6'));
      } else {
        toast.error(`${t('discountCodes.info7')} ${t('discountCodes.info8')}`);
      }
    }
  };

  const handleArchive = async () => {
    try {
      const { archived } = await put(`/discount-codes/${params.id}`, {
        archived: !isArchived,
      });

      if (archived) {
        history.push('/admin/discount-codes');
      } else {
        setIsArchived(false);
      }

      toast.info(
        archived ? t('discountCodes.info9') : t('discountCodes.info10')
      );
    } catch (error) {
      // eslint-disable-next-line no-console -- FIXME
      console.error(error);
    }
  };

  return (
    <form>
      <h2>
        {t(
          isEdit
            ? 'discountCodes.discountCodeEdit'
            : 'discountCodes.discountCodeCreate'
        )}
      </h2>
      <BasicInfoSection
        basicInfoSection={basicInfoSection}
        setBasicInfoSection={setBasicInfoSection}
        isEdit={isEdit}
        isFreeDelivery={scopeOfActivitySection.isFreeDelivery}
      />
      <PlatformsSection
        platforms={restrictionsSection.platforms}
        restrictPlatforms={restrictionsSection.restrictPlatforms}
        setRestrictionsSection={setRestrictionsSection}
        isPercent={basicInfoSection.isPercent}
        isEdit={isEdit}
      />
      <ScopeOfActivitySections
        scopeOfActivitySection={scopeOfActivitySection}
        setScopeOfActivitySection={setScopeOfActivitySection}
        diets={diets}
        isEdit={isEdit}
      />
      <RestrictionsSection
        restrictionsSection={restrictionsSection}
        setRestrictionsSection={setRestrictionsSection}
        startDateValidation={startDateValidation}
        endDateValidation={endDateValidation}
        isEdit={isEdit}
        brandConsentsAvailability={brandConsentsAvailability}
      />
      {!isLoading && (
        <GeolocationRestrictionsSection
          key={hasGeolocationRestrictions}
          hasGeolocationRestrictions={hasGeolocationRestrictions}
          setHasGeolocationRestrictions={setHasGeolocationRestrictions}
          importedDiscountCodes={importedDiscountCodes}
          setImportedDiscountCodes={setImportedDiscountCodes}
        />
      )}
      <FormControlStickyButton
        discardText={t('discountCodes.cancel')}
        submitText={t(isEdit ? 'form.save' : 'button.generateCode')}
        cancelPath="/admin/discount-codes"
        handleSubmit={performValidations}
        customButtonsLeft={
          isEdit
            ? [
                {
                  label: t(
                    isArchived
                      ? 'discountCodes.archive.restore'
                      : 'discountCodes.archive'
                  ),
                  className: classes.buttonArchive,
                  onClick: handleArchive,
                },
              ]
            : []
        }
      />

      <Dialog open={editedValuesToConfirm.length > 0}>
        <SweetAlert
          warning={true}
          style={{ width: 'fit-content' }}
          title={
            <span className={classes.sweetAlertTitle}>
              {t('alertTitle.saveChanges')}
            </span>
          }
          onConfirm={handleSubmit}
          onCancel={() => {
            setEditedValuesToConfirm([]);
          }}
          confirmBtnCssClass={classNames(classes.button, classes.success)}
          cancelBtnCssClass={classes.button}
          confirmBtnText={t('alertActions.confirmSave')}
          cancelBtnText={t('common.cancel')}
          showCancel={true}
        >
          <div className={classes.sweetAlertContent}>
            <Trans
              i18nKey="discountCodes.confirmChangesAlert"
              components={{
                b: <b />,
                span: <span />,
                list: (
                  <ul className={classes.valuesToConfirmList}>
                    {editedValuesToConfirm.map(
                      ({ key, descriptionI18nKey }) => (
                        <li key={key}>{t(descriptionI18nKey)}</li>
                      )
                    )}
                  </ul>
                ),
              }}
            />
          </div>
        </SweetAlert>
      </Dialog>
    </form>
  );
};

export default Form;
